export type ProjectCategoryType = string;
export type ProjectStatusType = 'completed' | 'in-progress';

export type ProjectImage = {
  src: string;
  alt?: string | null;
  title?: string | null;
};

export type ProjectBeforeAfterComparison = {
  title?: string | null;
  beforeSrc: string;
  afterSrc: string;
  beforeAlt?: string | null;
  afterAlt?: string | null;
};

export type SanityProject = {
  _id: string;
  title: string;
  location?: string | null;
  category?: {
    title?: string | null;
    slug?: string | null;
  } | null;
  type?: ProjectCategoryType | null;
  coverImage?: ProjectImage | null;
  description?: string | null;
  delivered?: string | null;
  duration?: string | null;
  status?: ProjectStatusType | null;
  beforeAfter: ProjectBeforeAfterComparison[];
  renders: ProjectImage[];
  images: ProjectImage[];
};

export const PROJECT_PLACEHOLDER_IMAGE: ProjectImage = {
  src: 'https://cdn.sanity.io/images/lv4fz89p/production/38a2825551087352d50d8cc689ba57529d8eb926-2400x1350.webp',
  alt: 'Land Corp placeholder image',
  title: 'Project image coming soon',
};

export function getProjectPhotoImages(project: SanityProject): ProjectImage[] {
  return project.images.length > 0
    ? project.images
    : project.coverImage
      ? [project.coverImage]
      : [];
}

export function formatProjectType(type?: ProjectCategoryType | null) {
  if (!type) return 'TBD';

  return type
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function formatProjectStatus(status?: ProjectStatusType | null) {
  if (!status) return 'TBD';
  if (status === 'in-progress') return 'In Progress';
  if (status === 'completed') return 'Completed';
  return 'TBD';
}

/** First plausible 4-digit calendar year (19xx–20xx) in the delivered label, if any. */
const DELIVERED_YEAR_PATTERN = /\b(19|20)\d{2}\b/;

function parseDeliveredYear(delivered?: string | null): number | null {
  const text = delivered?.trim();
  if (!text) return null;
  const match = text.match(DELIVERED_YEAR_PATTERN);
  if (!match) return null;
  return Number.parseInt(match[0], 10);
}

function compareProjectTitle(a: SanityProject, b: SanityProject): number {
  return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
}

/**
 * Homepage card sections (recent works, parallax, portfolio grid): in-progress first (A–Z),
 * then non–in-progress by delivered year descending (same year A–Z). If some have a parseable
 * year and some do not, dated projects come first; undated follow A–Z. If none have a year,
 * the remainder is A–Z. Only `status === 'in-progress'` is treated as in progress.
 */
export function sortProjectsForCardSections(projects: SanityProject[]): SanityProject[] {
  const inProgress: SanityProject[] = [];
  const other: SanityProject[] = [];
  for (const project of projects) {
    if (project.status === 'in-progress') inProgress.push(project);
    else other.push(project);
  }
  inProgress.sort(compareProjectTitle);

  const anyParsedYear = other.some((p) => parseDeliveredYear(p.delivered) !== null);
  if (!anyParsedYear) {
    other.sort(compareProjectTitle);
    return [...inProgress, ...other];
  }

  const withYear: { project: SanityProject; year: number }[] = [];
  const withoutYear: SanityProject[] = [];
  for (const project of other) {
    const year = parseDeliveredYear(project.delivered);
    if (year !== null) withYear.push({ project, year });
    else withoutYear.push(project);
  }
  withYear.sort((a, b) => b.year - a.year || compareProjectTitle(a.project, b.project));
  withoutYear.sort(compareProjectTitle);
  return [...inProgress, ...withYear.map((item) => item.project), ...withoutYear];
}

export function getProjectDisplayImage(project: SanityProject): ProjectImage | null {
  return (
    project.coverImage ??
    project.images[0] ??
    project.renders[0] ??
    getProjectBeforeAfterImages(project)[0] ??
    PROJECT_PLACEHOLDER_IMAGE
  );
}

export function getProjectBeforeAfterImages(project: SanityProject): ProjectImage[] {
  return project.beforeAfter.flatMap((comparison) => [
    {
      src: comparison.beforeSrc,
      alt: comparison.beforeAlt,
      title: comparison.title,
    },
    {
      src: comparison.afterSrc,
      alt: comparison.afterAlt,
      title: comparison.title,
    },
  ]);
}

export function getProjectGalleryImages(project: SanityProject): ProjectImage[] {
  return [
    ...getProjectPhotoImages(project),
    ...project.renders,
    ...getProjectBeforeAfterImages(project),
  ];
}
